import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import type { ConversationTurnEntry } from '@/features/events/conversation/state/types'
import type { AskUserQuestionToolInput } from '@/features/events/shared/types'
import { BaseBubble } from '@/features/events/conversation/bubbles/BaseBubble'

interface DemoQuestionsBubbleProps {
  readonly event: ConversationTurnEntry
  readonly selectedIndex: number | undefined
}

export const DemoQuestionsBubble: FC<DemoQuestionsBubbleProps> = ({ event, selectedIndex }) => {
  const theme = useTheme()
  const toolInput = event.notificationContext?.toolInput as AskUserQuestionToolInput | undefined
  const questions = toolInput?.questions ?? []

  if (questions.length === 0) {
    return null
  }

  const question = questions[0]
  if (!question) {
    return null
  }

  const hasSelection = selectedIndex !== undefined

  return (
    <BaseBubble event={event} showTimestamp={true}>
      <Box sx={contentContainerSx}>
        <Box sx={headerContainerSx}>
          <Typography variant="h6" sx={mainHeaderSx}>
            Answer Questions
          </Typography>
        </Box>

        <Box sx={questionContainerSx}>
          <Box sx={fieldContainerSx}>
            <Typography variant="caption" sx={questionNumberSx}>
              Question 1/1
            </Typography>
            <Typography variant="subtitle1" sx={questionTextSx}>
              {question.question}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={questionHeaderLabelSx}>
              {question.header}
            </Typography>
          </Box>

          {question.options.map((option, optionIndex) => {
            const isSelected = selectedIndex === optionIndex + 1
            const isDisabled = hasSelection && !isSelected

            return (
              <Box key={option.label} sx={optionRowSx}>
                <Box sx={optionButtonSx}>
                  <Typography component="span" sx={indexBadgeSx(isSelected, isDisabled, theme)}>
                    {optionIndex + 1}
                  </Typography>
                  <Box sx={optionLabelContainerSx}>
                    <Typography variant="body1" sx={optionLabelSx(isSelected, isDisabled, theme)}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" sx={optionDescriptionSx(isDisabled)}>
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Box>

        {!hasSelection && (
          <Box sx={hintContainerSx}>
            <Typography variant="caption" sx={hintTextSx}>
              Press 1, 2, 3 or click to select
            </Typography>
          </Box>
        )}
      </Box>
    </BaseBubble>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

const headerContainerSx: SxProps<Theme> = {
  mb: 1,
}

const mainHeaderSx: SxProps<Theme> = {
  fontWeight: 500,
  mb: 1,
}

const questionContainerSx: SxProps<Theme> = {
  p: 3,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
}

const fieldContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  mb: 2,
}

const questionNumberSx: SxProps<Theme> = {
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.secondary',
  fontSize: 'inherit',
}

const questionTextSx: SxProps<Theme> = {
  fontWeight: 500,
  mt: 0.5,
  mb: 1,
  fontSize: 'inherit',
}

const questionHeaderLabelSx: SxProps<Theme> = {
  fontStyle: 'italic',
  color: 'text.secondary',
  fontSize: 'inherit',
}

const optionRowSx: SxProps<Theme> = {
  mb: 1.5,
}

const optionButtonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  textAlign: 'left',
  borderRadius: 1,
  p: 1,
}

const indexBadgeSx = (isSelected: boolean, isDisabled: boolean, theme: Theme): SxProps<Theme> => ({
  minWidth: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  color: optionIconColor(isSelected, isDisabled, theme),
  fontWeight: 700,
  mr: 1,
  flexShrink: 0,
  fontSize: 'inherit',
})

const optionLabelContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
}

const optionLabelSx = (isSelected: boolean, isDisabled: boolean, theme: Theme): SxProps<Theme> => ({
  fontWeight: isSelected ? 600 : 400,
  color: optionLabelColor(isSelected, isDisabled, theme),
})

const optionDescriptionSx = (isDisabled: boolean): SxProps<Theme> => ({
  lineHeight: 1.5,
  color: isDisabled ? 'text.disabled' : 'text.secondary',
  fontSize: 'inherit',
})

const hintContainerSx: SxProps<Theme> = {
  mt: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}

const hintTextSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontStyle: 'italic',
  opacity: 0.6,
  fontSize: '0.65rem',
}

function optionIconColor(isSelected: boolean, isDisabled: boolean, theme: Theme): string {
  if (isSelected) return theme.palette.warning.main
  if (isDisabled) return theme.palette.text.disabled
  return theme.palette.warning.main
}

function optionLabelColor(isSelected: boolean, isDisabled: boolean, theme: Theme): string {
  if (isSelected) return theme.palette.warning.main
  if (isDisabled) return theme.palette.text.disabled
  return theme.palette.text.primary
}
